// F76 (Pavel 2026-08-06): the design question is a structured collection -
// own links, uploaded files, a described feel, example sites. ONE normalizer
// serves the runner's edit state, the completeness rule and the DFY export,
// and it reads every LEGACY value (style_source / design_feel / inspiration)
// so stored answers keep working: URLs become links, prose folds into the
// description. Nothing is rewritten in D1; migration happens on read and the
// first client edit persists the new shape.

export interface DesignFileRef {
  id: string;
  name: string | null;
}

export interface DesignRefs {
  own_links: string[];
  files: DesignFileRef[];
  description: string;
  example_links: string[];
  /** F87: the client explicitly leaves the design to the team. */
  defer_to_team: boolean;
}

const URL_PATTERN = /https?:\/\/\S+/gi;

export function isHttpUrl(value: string): boolean {
  return /^https?:\/\/\S+$/i.test(value.trim());
}

/** URLs inside free text become links; what remains is prose. */
export function splitLinksFromText(value: unknown): { links: string[]; prose: string } {
  if (typeof value !== "string" || !value.trim()) return { links: [], prose: "" };
  const links = [...value.matchAll(URL_PATTERN)].map((match) => match[0].replace(/[.,;)\]]+$/, ""));
  const prose = value.replace(URL_PATTERN, " ").replace(/\s+/g, " ").trim();
  return { links, prose };
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string" && entry.trim().length > 0) : [];
}

function fileRefs(value: unknown): DesignFileRef[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) return [];
    const row = entry as Record<string, unknown>;
    if (typeof row.id !== "string" || !row.id) return [];
    return [{ id: row.id, name: typeof row.name === "string" ? row.name : null }];
  });
}

function dedupe(values: string[]): string[] {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))];
}

/**
 * The one reading of a q2_52 answer value, whatever its era. New-shape keys
 * win; legacy keys merge in without duplicating a link that was already
 * migrated into the lists.
 */
export function normalizeDesignRefs(value: Record<string, unknown> | null | undefined): DesignRefs {
  const raw = value ?? {};
  const own = stringArray(raw.own_links);
  const examples = stringArray(raw.example_links);
  const styleSource = splitLinksFromText(raw.style_source);
  const inspiration = splitLinksFromText(raw.inspiration);
  const proseParts = [
    typeof raw.design_feel === "string" ? raw.design_feel.trim() : "",
    styleSource.prose,
    inspiration.prose
  ].filter(Boolean);
  return {
    own_links: dedupe([...own, ...styleSource.links]),
    files: fileRefs(raw.attachments),
    description: proseParts.join("\n").trim(),
    example_links: dedupe([...examples, ...inspiration.links]),
    defer_to_team: raw.defer_to_team === true
  };
}

/**
 * The value the runner edits: the normalized shape written back under the
 * NEW keys, legacy keys dropped so a later normalize cannot double-count
 * them. The voice recording companion survives untouched.
 */
export function designEditValue(value: Record<string, unknown> | null | undefined): Record<string, unknown> {
  const refs = normalizeDesignRefs(value);
  const recording = value?.design_feel_recording;
  return {
    own_links: refs.own_links,
    example_links: refs.example_links,
    design_feel: refs.description,
    attachments: refs.files.map((file) => ({ id: file.id, ...(file.name ? { name: file.name } : {}) })),
    ...(refs.defer_to_team ? { defer_to_team: true } : {}),
    ...(typeof recording === "string" && recording ? { design_feel_recording: recording } : {})
  };
}

export function designRefsComplete(value: Record<string, unknown> | null | undefined): boolean {
  const refs = normalizeDesignRefs(value);
  // F87: "Nechám to na vás" is an explicit, stored answer - the question is
  // complete even with every field empty; the team chooses the design.
  return refs.defer_to_team || refs.description.length > 0 || refs.own_links.length > 0 ||
    refs.files.length > 0 || refs.example_links.length > 0;
}
